import React, { useEffect, useRef, useState } from 'react';
import DeluxePaymentTabSkeleton from '../embedded/wallets/payment-method-tabs/DeluxePaymentTabSkeleton';
import '../embedded/wallets/payment-method-tabs/deluxe-booking-payment-tabs.css';
import './DeluxeHostedPaymentForm.css';

const SCRIPT_ID = 'deluxe-hosted-form-sdk';

const XRTYPE = {
  cryptogram: 'Generate Cryptogram',
  token: 'Generate Token',
  vault: 'Create Vault',
};

function asRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function readHostedForm() {
  try {
    return new Function('return typeof HostedForm !== "undefined" ? HostedForm : null')();
  } catch {
    return null;
  }
}

function loadHostedFormScript(scriptUrl) {
  return new Promise((resolve, reject) => {
    const ready = readHostedForm();
    if (ready?.init) {
      resolve(ready);
      return;
    }
    const finish = () => {
      const sdk = readHostedForm();
      if (sdk?.init) resolve(sdk);
      else reject(new Error('Deluxe hosted payment form is unavailable'));
    };
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        finish();
        return;
      }
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Deluxe hosted payment form')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      finish();
    };
    script.onerror = () => reject(new Error('Failed to load Deluxe hosted payment form'));
    document.head.appendChild(script);
  });
}

function firstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function extractHpfResult(data) {
  const payload = asRecord(data);
  const inner = typeof payload.data === 'string' ? {} : asRecord(payload.data);
  const cryptogram = firstString(
    typeof payload.data === 'string' ? payload.data : '',
    inner.Cryptogram,
    inner.cryptogram,
    inner.cryptograrm,
    inner.CryptoGram,
    payload.Cryptogram,
    payload.cryptogram
  );
  const token = firstString(inner.token, inner.Token, payload.token);
  const customerId = firstString(inner.customerId, inner.CustomerToken, inner.customerToken);
  const paymentMethodId = firstString(inner.vaultId, inner.VaultId, inner.paymentMethodId);
  return {
    cryptogram: cryptogram || token,
    token,
    customerId,
    paymentMethodId,
    last4: String(inner.maskedPAN || inner.MaskedPan || inner.maskedPan || token).replace(/\D/g, '').slice(-4),
  };
}

function extractHpfError(data, fallback) {
  const payload = asRecord(data);
  if (Array.isArray(payload.errors) && payload.errors.length) return payload.errors.join(' ');
  if (typeof payload.errors === 'string' && payload.errors.trim()) return payload.errors.trim();
  return firstString(payload.message, payload.error, fallback);
}

const DeluxeHostedPaymentForm = ({
  hpfToken,
  scriptUrl,
  mode = 'cryptogram',
  amount,
  onSuccess,
  onError,
  disabled = false,
}) => {
  const containerIdRef = useRef(`deluxe-hpf-${Math.random().toString(36).slice(2, 10)}`);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    if (!hpfToken || !scriptUrl || disabled) return undefined;

    setReady(false);
    setError('');

    loadHostedFormScript(scriptUrl)
      .then((HostedFormSdk) => {
        if (cancelled || !HostedFormSdk) {
          throw new Error('Deluxe hosted payment form is unavailable');
        }
        const options = {
          containerId: containerIdRef.current,
          xtoken: hpfToken,
          xrtype: XRTYPE[mode] || XRTYPE.cryptogram,
          xbtntext: 'Pay',
          xbtncctext: 'Card',
          xpm: '1',
        };
        const numericAmount = Number(amount);
        if (Number.isFinite(numericAmount) && numericAmount > 0) {
          options.xAmount = numericAmount.toFixed(2);
          options.xbtntext = `Pay $${numericAmount.toFixed(2)}`;
        }
        return HostedFormSdk.init(options, {
          onSuccess: (data) => {
            if (cancelled) return;
            const result = extractHpfResult(data);
            if (!result.cryptogram && !result.paymentMethodId && !result.token) {
              const message = 'Deluxe did not return a payment token';
              setError(message);
              onErrorRef.current?.(new Error(message));
              return;
            }
            onSuccessRef.current?.(result);
          },
          onFailure: (data) => {
            const message = extractHpfError(data, 'Payment failed');
            setError(message);
            onErrorRef.current?.(new Error(message));
          },
          onInvalid: (data) => {
            setError(extractHpfError(data, 'Please check the card details'));
          },
        }).then((instance) => {
          if (cancelled) return instance;
          if (typeof instance?.renderHpf === 'function') return instance.renderHpf();
          if (typeof instance?.render === 'function') return instance.render();
          return instance;
        });
      })
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err?.message || 'Could not load Deluxe hosted payment form';
        setError(message);
        onErrorRef.current?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [hpfToken, scriptUrl, mode, amount, disabled]);

  return (
    <div className="deluxe-hpf" aria-busy={!ready && !error}>
      {error && (
        <div className="deluxe-hpf__error" role="alert">
          {error}
        </div>
      )}
      <div className="deluxe-hpf__stage">
        {!ready && !error ? <DeluxePaymentTabSkeleton variant="card" /> : null}
        <div
          id={containerIdRef.current}
          className={`deluxe-hpf__frame ${ready ? '' : 'deluxe-hpf__frame--pending'}`}
        />
      </div>
    </div>
  );
};

export default DeluxeHostedPaymentForm;
